<template>
  <div class="kanban-component">
    <div v-if="$can('update', 'Dashboard_' + currentDash)" class="mb-3">
      <b-button variant="primary" size="sm" @click="editOptions">
        <icon name="cog" />
        <span>Edit Options</span>
      </b-button>
    </div>
    <TableHeadingFilterBuilder
      v-if="filterBuilder"
      class="widgetContent__filters"
      :filter="filterBuilder"
      :gridId="options.itemsStorage"
      @change="applyBuilderFilters(filterBuilder)"
    />
    <board
      v-if="options && lanes && loaded"
      :lanes="lanes"
      :items="items"
      @item-created="handleItemCreated"
      @item-updated="handleItemUpdated"
      ref="board"
    >
      <template v-slot:default="props">
        <div
          class="kanban-item"
          :data-item-key="props.data.rowKey"
          :data-lane-key="props.data.status"
        >
          <div class="kb-card-header">
            <b>{{ props.data[options.titleCol] }}</b>
            <b-button
              variant="primary"
              size="sm pull-right"
              style="margin-right: 0;"
              @click="openDataGridModal(options.itemsStorage, props.data.rowKey)"
            >
              <span>Open</span>
            </b-button>
            <hr style="margin: 10px 0 15px 0;" />
          </div>
        </div>
        <DynamicRender
          :template="options.template"
          :data="{ data: props.data, item: props.data, row: props.data }"
          :computed="{
            openItem: kanbanItemFunction(options.itemsStorage, props.data.rowKey)
          }"
        />
      </template>
    </board>
    <div v-else>
      <h2 v-if="!options">Loading...</h2>
    </div>
    <EditKanbanModal v-if="options" :options="options" :kanbanId="kanbanId" ref="editModal" />
    <DynamicRender v-if="onChangeCode" :template="onChangeCode" :data="{}" :computed="{}" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { filter, sortBy, toArray, orderBy } from 'lodash'
import filterUtils from '@/helpers/filter'
import Board from '@salamander.be/vue-kanban-board'
import '@salamander.be/vue-kanban-board/dist/vue-kanban-board.css'
import { v1 as uuidv1 } from 'uuid'
// services
import db from '@/services/db'
// components
import DynamicRender from '@/components/DynamicRender'
import EditKanbanModal from './modals/EditKanban'
import TableHeadingFilterBuilder from '@/components/DataGrid/table/TableHeadingFilterBuilder'

export default {
  props: ['kanbanId'],
  components: {
    Board,
    DynamicRender,
    EditKanbanModal,
    TableHeadingFilterBuilder,
  },
  data() {
    return {
      options: null,
      loaded: false,
      onChangeCode: null,
      filterBuilder: null,
    }
  },
  computed: {
    ...mapState(['currentDash', 'dataGrid_options', 'models_dashboardModel']),
    itemsStorage() {
      return $getGrid(this.options.itemsStorage)
    },
    lanesStorage() {
      return $dataGrid(this.options.lanesStorage)
    },
    items() {
      const { filterBuilder = '{}', meta = {} } =
        this.dataGrid_options[this.options.itemsStorage] || {}
      if (!this.filterBuilder) {
        const data = JSON.parse(filterBuilder)
        this.filterBuilder = data.rules && data.rules.length > 0 ? data : null
      }
      const filter = filterUtils.filterBuilderToFilter(this.filterBuilder)
      const itemsStorage = toArray(
        filterUtils.filterData(this.itemsStorage, filter, meta)
      )

      const items = []

      if (!itemsStorage) {
        this.items = items
        return
      }

      for (let item of itemsStorage) {
        items.push({
          ...item,
          status: item[this.options.statusCol] || false,
          id: item.rowKey,
        })
      }

      return sortBy(items, [this.options.sortIndexCol])
    },
    lanes() {
      let lanes = []

      if (!this.lanesStorage) {
        this.lanes = lanes
        return
      }

      for (let rowKey in this.lanesStorage) {
        const name = this.lanesStorage[rowKey][this.options.laneCol]

        lanes.push({
          name,
          slug: rowKey,
        })
      }

      lanes = orderBy(lanes, ['name'])

      lanes.unshift({ name: 'Default', slug: false })

      return lanes
    },
  },
  methods: {
    applyBuilderFilters(filterBuilder) {
      this.filterBuilder = filterBuilder
    },
    async handleItemCreated(newItem) {
      this.$nextTick(() => {
        $(this.$refs.board.$el)
          .find('.kb-card')
          .map((idx, domItem) => {
            const kanbanItem = $(domItem).find('.kanban-item')
            if (!$(kanbanItem).attr('data-item-key')) $(domItem).remove()
          })
      })

      const id = await db.dataGrid.data.getLastId(this.options.itemsStorage)
      const itemToAdd = {
        _id: id,
        rowKey: uuidv1(),
        [this.options.statusCol]: newItem.status,
        [this.options.titleCol]: newItem.summary,
      }

      const laneItems = filter(this.items, item => {
        return item[this.options.statusCol] === newItem.status
      })

      laneItems.unshift(itemToAdd)

      const updatedGrid = {}

      for (let idx in laneItems) {
        if (idx === 'rmIndex') continue

        const row = laneItems[idx]
        updatedGrid[row.rowKey] = row
        updatedGrid[row.rowKey][this.options.sortIndexCol] = parseInt(idx)
      }

      db.dataGrid.data.updateGrid(this.options.itemsStorage, updatedGrid)
    },
    handleItemUpdated(rowKey, newStatus, index) {
      const itemsStorage = $dataGrid(this.options.itemsStorage)
      const curStatus = itemsStorage[rowKey][this.options.statusCol]

      this.$nextTick(() => {
        const lane1Items = {}
        const lane2Items = {}

        if (curStatus !== newStatus) {
          const items = $(
            `.drag-inner-list[data-status="${newStatus}"] .kb-card`
          )

          items.map((idx, item) => {
            const itemRowKey = $(item).data('block-id')

            lane1Items[itemRowKey] = Object.assign(itemsStorage[itemRowKey], {
              [this.options.sortIndexCol]: idx,
              [this.options.statusCol]: curStatus,
            })
          })
        }

        const items = newStatus
          ? $(`.drag-inner-list[data-status="${newStatus}"] .kb-card`)
          : $(`.drag-column-false .kb-card`)

        items.map((idx, item) => {
          const itemRowKey = $(item).data('block-id')

          lane1Items[itemRowKey] = Object.assign(itemsStorage[itemRowKey], {
            [this.options.sortIndexCol]: idx,
            [this.options.statusCol]: newStatus || null,
          })
        })

        db.dataGrid.data.updateGrid(
          this.options.itemsStorage,
          Object.assign(lane1Items, lane2Items)
        )
      })
    },
    editOptions() {
      this.$refs.editModal.open()
    },
    openDataGridModal(gridId, rowKey) {
      $dgShowEditRowModal(gridId, rowKey)
    },
    kanbanItemFunction(gridId, rowKey) {
      return () => {
        this.openDataGridModal(gridId, rowKey)
      }
    },
  },
  async created() {
    db.kanban.readOptions(this.kanbanId, options => {
      this.options = options
      this.loaded = true
    })
  },
}
</script>

<style lang="scss">
.kanban-component {
  .kb-card > div {
    display: block !important;
    > .widgetContent > div {
      height: auto !important;
    }
  }
}
</style>
